import { IconType } from 'react-icons';

import { NotificationKind, Priority } from '@this+that/models';
import {
  LuBrain,
  LuClipboardCheck,
  LuMail,
  LuShuffle,
  LuSquareCheck,
  LuUnplug,
  LuUsers,
} from 'react-icons/lu';

export const NOTIFICATION_SOURCE_SERVICE = {
  TASKS: 'tasks',
  INTEGRATIONS: 'integrations',
  FLOWS: 'flows',
  EMAIL: 'email',
  TEAMS: 'teams',
  BRAIN: 'brain',
  NEEDS_DECISION: 'needs_decision',
} as const;

export type NotificationSourceServiceKey =
  (typeof NOTIFICATION_SOURCE_SERVICE)[keyof typeof NOTIFICATION_SOURCE_SERVICE];

const PALETTE = {
  teal: '#0d9488',
  tealSoft: 'rgba(13, 148, 136, 0.14)',
  violet: '#8b5cf6',
  blue: '#0284c7',
  amber: '#d97706',
  pink: '#ec4899',
  indigo: '#6366f1',
  red: '#ff6b6b',
  redSoft: 'rgba(255, 107, 107, 0.12)',
  amberSoft: 'rgba(255, 184, 77, 0.12)',
  blueSoft: 'rgba(94, 200, 255, 0.12)',
  slate: '#7a9794',
  slateSoft: 'rgba(122, 151, 148, 0.10)',
} as const;

export const DESIGN_TOKENS = {
  accent: PALETTE.teal,
  accentDim: PALETTE.tealSoft,
} as const;

export interface SourceMeta {
  icon: IconType;
  label: string;
  color: string;
}

export const SOURCE_META: Record<string, SourceMeta> = {
  [NOTIFICATION_SOURCE_SERVICE.TASKS]: {
    icon: LuSquareCheck,
    label: 'Tasks',
    color: PALETTE.teal,
  },
  [NOTIFICATION_SOURCE_SERVICE.INTEGRATIONS]: {
    icon: LuUnplug,
    label: 'Integrations',
    color: PALETTE.violet,
  },
  [NOTIFICATION_SOURCE_SERVICE.FLOWS]: {
    icon: LuShuffle,
    label: 'Flows',
    color: PALETTE.blue,
  },
  [NOTIFICATION_SOURCE_SERVICE.EMAIL]: {
    icon: LuMail,
    label: 'Email',
    color: PALETTE.amber,
  },
  [NOTIFICATION_SOURCE_SERVICE.TEAMS]: {
    icon: LuUsers,
    label: 'Teams',
    color: PALETTE.pink,
  },
  [NOTIFICATION_SOURCE_SERVICE.BRAIN]: {
    icon: LuBrain,
    label: 'Brain',
    color: PALETTE.indigo,
  },
  [NOTIFICATION_SOURCE_SERVICE.NEEDS_DECISION]: {
    icon: LuClipboardCheck,
    label: 'Needs your decision',
    color: PALETTE.teal,
  },
};

export const DEFAULT_SOURCE_META: SourceMeta = {
  icon: LuSquareCheck,
  label: 'Other',
  color: PALETTE.teal,
};

export interface PriorityMeta {
  color: string;
  bg: string;
  label: string;
}

export const PRIORITY_META: Record<Priority, PriorityMeta> = {
  [Priority.CRITICAL]: {
    color: PALETTE.red,
    bg: PALETTE.redSoft,
    label: 'Urgent',
  },
  [Priority.HIGH]: {
    color: PALETTE.amber,
    bg: PALETTE.amberSoft,
    label: 'High',
  },
  [Priority.MEDIUM]: {
    color: PALETTE.blue,
    bg: PALETTE.blueSoft,
    label: 'Medium',
  },
  [Priority.LOW]: {
    color: PALETTE.slate,
    bg: PALETTE.slateSoft,
    label: 'Low',
  },
};

export const KIND_SOURCE_MAP: Record<NotificationKind, string> = {
  [NotificationKind.TEAM_INVITATION_PENDING]: NOTIFICATION_SOURCE_SERVICE.TEAMS,
  [NotificationKind.DOBOX_GENERATED]: NOTIFICATION_SOURCE_SERVICE.TASKS,
  [NotificationKind.DOBOX_ASSIGNED]: NOTIFICATION_SOURCE_SERVICE.TASKS,
  [NotificationKind.DOBOX_COMMENT_MENTION]: NOTIFICATION_SOURCE_SERVICE.TASKS,
  [NotificationKind.TASK_GROUP_ADDED]: NOTIFICATION_SOURCE_SERVICE.TASKS,
  [NotificationKind.KNOWLEDGE_PAGE_MENTION]: NOTIFICATION_SOURCE_SERVICE.BRAIN,
  [NotificationKind.SYNC_FAILED]: NOTIFICATION_SOURCE_SERVICE.INTEGRATIONS,
  [NotificationKind.GOOGLE_REVOKE_FAILED]:
    NOTIFICATION_SOURCE_SERVICE.INTEGRATIONS,
};

// A kind with its own title here is a kind the alert renders as a failure: an integration
// is degraded and the user is the one who has to act.
export const ALERT_TITLE_KEY_BY_KIND: Partial<
  Record<NotificationKind, string>
> = {
  [NotificationKind.SYNC_FAILED]:
    'components.gradientAlert.syncFailedAlertTitle',
  [NotificationKind.GOOGLE_REVOKE_FAILED]:
    'components.gradientAlert.googleRevokeFailedAlertTitle',
};

// Derived, never hand-maintained: two lists of the same fact drift into a warning glyph
// under a generic title, or a failure title under a bell, and neither shape fails a test.
export const ALERT_FAILURE_KINDS: ReadonlySet<NotificationKind> = new Set(
  Object.keys(ALERT_TITLE_KEY_BY_KIND) as NotificationKind[],
);

export const DEFAULT_ALERT_TITLE_KEY =
  'components.gradientAlert.notificationAlertTitle';

export const NOTIFICATION_TAB = {
  UNREAD: 'unread',
  READ: 'read',
} as const;

export type NotificationTab =
  (typeof NOTIFICATION_TAB)[keyof typeof NOTIFICATION_TAB];

export const POPOVER_WIDTH = 430;
export const POPOVER_MAX_HEIGHT = 600;
export const POPOVER_VIEWPORT_MARGIN = 8;
export const SECTION_HEADER_HEIGHT_PX = 42;
export const UNREAD_BADGE_MAX = 9;
export const UNREAD_BADGE_OVERFLOW_LABEL = '9+';
